import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import DOMPurify from 'dompurify';
import {
  PromptTemplate,
  PromptRun,
  CreateTemplateParams,
} from './types';
import {
  fetchTemplates,
  fetchAllTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  runPrompt,
  runPromptWithFile,
  checkOpenAiStatus,
  fetchHistory,
  fetchHistoryItem,
  fetchTags,
} from './promptsApi';
import { copyPlainTextToClipboard, copyRichTextToClipboard } from './clipboard';
import { promptTextToHtml, promptTextToRichContent, promptTextsToRichContent } from './formatting';
import { hasPermission } from '../../utils/permissionUtils';
import { IUser } from '../../types/damTypes';
import PromptFilePicker, { PromptAttachedFile } from './PromptFilePicker';

interface PromptsPageProps {
  onClose: () => void;
  currentUser: IUser | null;
}

// Helper: Convert to sentence case (first letter uppercase, rest lowercase)
const toSentenceCase = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const DEFAULT_PROMPT_MODEL = 'gpt-5.5';
const DEFAULT_MAX_OUTPUT_TOKENS = 4096;

const getOutputFormatLabel = (format: PromptTemplate['outputFormat']): string => {
  if (format === 'markdown') return 'Formaterad text';
  if (format === 'json') return 'JSON';
  return 'Text';
};

// Helper: Convert Markdown to TipTap-compatible HTML for the rich text editor
const markdownToEditorHtml = (md: string): string => {
  if (!md) return '';

  const inlineFmt = (text: string): string =>
    text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');

  const lines = md.split('\n');
  const parts: string[] = [];
  let inList = false;
  let listTag: 'ul' | 'ol' = 'ul';

  const closeList = () => {
    if (inList) {
      parts.push(`</${listTag}>`);
      inList = false;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();

    if (!trimmed) {
      closeList();
      continue;
    }

    // Headings
    const hMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (hMatch) {
      closeList();
      const level = hMatch[1].length;
      parts.push(`<h${level}>${inlineFmt(hMatch[2])}</h${level}>`);
      continue;
    }

    // Bullet list (handles •, -, and * followed by space)
    const bulletMatch = trimmed.match(/^[-•]\s+(.+)$/) || trimmed.match(/^\*\s+(.+)$/);
    if (bulletMatch) {
      if (!inList || listTag !== 'ul') {
        closeList();
        parts.push('<ul>');
        inList = true;
        listTag = 'ul';
      }
      parts.push(`<li><p>${inlineFmt(bulletMatch[1])}</p></li>`);
      continue;
    }

    // Numbered list
    const numMatch = trimmed.match(/^\d+\.\s+(.+)$/);
    if (numMatch) {
      if (!inList || listTag !== 'ol') {
        closeList();
        parts.push('<ol>');
        inList = true;
        listTag = 'ol';
      }
      parts.push(`<li><p>${inlineFmt(numMatch[1])}</p></li>`);
      continue;
    }

    // Regular paragraph
    closeList();
    parts.push(`<p>${inlineFmt(trimmed)}</p>`);
  }

  closeList();
  return DOMPurify.sanitize(parts.join(''));
};

const PromptsPage: React.FC<PromptsPageProps> = ({ onClose, currentUser }) => {
  // State
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openAiAvailable, setOpenAiAvailable] = useState(false);

  // Search/filter
  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);

  // Selected template
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | null>(null);

  // Input/Output
  const [inputText, setInputText] = useState('');
  const [attachedFile, setAttachedFile] = useState<PromptAttachedFile | null>(null);
  const [runVariants, setRunVariants] = useState<number>(1);
  const [outputTexts, setOutputTexts] = useState<string[]>([]);
  const [generatingGpt, setGeneratingGpt] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [generationWarning, setGenerationWarning] = useState<string | null>(null);
  const [usedModel, setUsedModel] = useState<string | null>(null);
  const [outputMode, setOutputMode] = useState<'formatted' | 'raw'>('formatted');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  // History
  const [history, setHistory] = useState<PromptRun[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Admin dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
  const [saving, setSaving] = useState(false);

  // Permissions
  const isAdmin = hasPermission(currentUser, 'prompts:admin');
  const canUsePrompts = isAdmin || hasPermission(currentUser, 'prompts:use');

  // Load templates
  const loadTemplates = useCallback(async (opts?: { search?: string; tag?: string }) => {
    if (!canUsePrompts) {
      setTemplates([]);
      setSelectedTemplate(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = isAdmin
        ? await fetchAllTemplates()
        : await fetchTemplates({ search: opts?.search, tag: opts?.tag });
      setTemplates(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte hämta promptar');
    } finally {
      setLoading(false);
    }
  }, [canUsePrompts, isAdmin]);

  // Load tags
  const loadTags = useCallback(async () => {
    if (!canUsePrompts) {
      setAllTags([]);
      return;
    }
    try {
      const tags = await fetchTags();
      setAllTags(tags);
    } catch {
      // Ignore
    }
  }, [canUsePrompts]);

  // Check AI status
  useEffect(() => {
    if (!canUsePrompts) {
      setOpenAiAvailable(false);
      return;
    }
    checkOpenAiStatus()
      .then((r) => {
        setOpenAiAvailable(r.openai ?? r.available);
      })
      .catch(() => {
        setOpenAiAvailable(false);
      });
    loadTags();
  }, [canUsePrompts, loadTags]);

  // Load templates on mount / role change
  useEffect(() => {
    loadTemplates({ search: search || undefined, tag: selectedTag || undefined });
  }, [isAdmin, loadTemplates]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isAdmin) {
        loadTemplates({ search: search || undefined, tag: selectedTag || undefined });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, selectedTag, isAdmin, loadTemplates]);

  // Default "run variants" from selected template (max 3 in UI)
  useEffect(() => {
    if (!selectedTemplate) {
      setRunVariants(1);
      return;
    }
    const v = Number(selectedTemplate.variants || 1);
    setRunVariants(Math.min(Math.max(v, 1), 3));
  }, [selectedTemplate]);

  // Load history
  const loadHistory = useCallback(async () => {
    if (!canUsePrompts) {
      setHistory([]);
      return;
    }
    try {
      setHistoryLoading(true);
      const data = await fetchHistory({ limit: 30 });
      setHistory(data);
    } catch {
      // Ignore
    } finally {
      setHistoryLoading(false);
    }
  }, [canUsePrompts]);

  // Run prompt (GPT)
  const handleGenerate = async () => {
    if (!selectedTemplate || (!inputText.trim() && !attachedFile)) return;

    try {
      setGeneratingGpt(true);
      setGenerationError(null);
      setGenerationWarning(null);
      setOutputTexts([]);
      setUsedModel(null);

      const result = attachedFile
        ? await runPromptWithFile({
            templateId: selectedTemplate._id,
            file: attachedFile.file,
            pages: attachedFile.kind === 'pdf' ? attachedFile.pageNumbers : undefined,
            crop: attachedFile.kind === 'pdf' ? attachedFile.cropRegions : undefined,
            inputText: inputText.trim() || undefined,
            variants: runVariants,
          })
        : await runPrompt({
            templateId: selectedTemplate._id,
            inputText: inputText.trim(),
            variants: runVariants,
          });

      setOutputTexts(result.outputTexts);
      setUsedModel(result.model);
      if (result.incomplete) {
        setGenerationWarning(
          `Svaret kan vara avklippt eftersom modellen stoppade vid maxgränsen (${result.finishReason || 'max output tokens'}). Höj svarslängden och generera om.`,
        );
      }
    } catch (err: any) {
      setGenerationError(err?.response?.data?.message || err.message || 'Kunde inte generera text');
    } finally {
      setGeneratingGpt(false);
    }
  };

  // Copy helpers
  const copyWithFeedback = async (task: () => Promise<void>, successMessage: string) => {
    try {
      await task();
      setCopyMessage(successMessage);
    } catch {
      setCopyMessage('Kunde inte kopiera till urklipp');
    }
  };

  const handleCopyForWord = async (text: string, label = 'formaterad text') => {
    const content = promptTextToRichContent(text);
    await copyWithFeedback(
      () => copyRichTextToClipboard(content.html, content.plainText),
      `Kopierat som ${label}`,
    );
  };

  const handleCopyHtml = async (text: string) => {
    await copyWithFeedback(
      () => copyPlainTextToClipboard(promptTextToHtml(text)),
      'HTML kopierad',
    );
  };

  const handleCopyRaw = async (text: string) => {
    await copyWithFeedback(
      () => copyPlainTextToClipboard(text),
      'Råtext kopierad',
    );
  };

  const handleCopyAllForWord = async () => {
    const content = promptTextsToRichContent(outputTexts);
    await copyWithFeedback(
      () => copyRichTextToClipboard(content.html, content.plainText),
      'Alla svar kopierade som formaterad text',
    );
  };

  const handleCopyAllHtml = async () => {
    const content = promptTextsToRichContent(outputTexts);
    await copyWithFeedback(
      () => copyPlainTextToClipboard(content.html),
      'All HTML kopierad',
    );
  };

  const handleCopyAllRaw = async () => {
    await copyWithFeedback(
      () => copyPlainTextToClipboard(outputTexts.join('\n\n---\n\n')),
      'All råtext kopierad',
    );
  };

  // Load history item and populate input/output
  const handleLoadHistoryItem = async (run: PromptRun) => {
    try {
      const fullRun = await fetchHistoryItem(run._id);
      if (fullRun.inputText) setInputText(fullRun.inputText);
      if (fullRun.outputTexts) setOutputTexts(fullRun.outputTexts);
      setGenerationWarning(null);
      // Try to select the template
      const template = templates.find((t) => t._id === fullRun.templateId);
      if (template) setSelectedTemplate(template);
      setShowHistory(false);
    } catch {
      // Ignore
    }
  };

  // Create template
  const handleCreateTemplate = async (params: CreateTemplateParams) => {
    try {
      setSaving(true);
      await createTemplate(params);
      setShowCreateDialog(false);
      loadTemplates({ search: search || undefined, tag: selectedTag || undefined });
      loadTags();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Kunde inte skapa prompt');
    } finally {
      setSaving(false);
    }
  };

  // Update template
  const handleUpdateTemplate = async (params: CreateTemplateParams) => {
    if (!editingTemplate) return;
    try {
      setSaving(true);
      const updated = await updateTemplate(editingTemplate._id, params);
      setShowEditDialog(false);
      setEditingTemplate(null);
      loadTemplates({ search: search || undefined, tag: selectedTag || undefined });
      loadTags();
      // Update selected if it was the one we edited
      if (selectedTemplate?._id === updated._id) {
        setSelectedTemplate(updated);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Kunde inte uppdatera prompt');
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (template: PromptTemplate) => {
    if (!window.confirm(`Vill du verkligen inaktivera "${template.name}"?`)) return;
    try {
      await deleteTemplate(template._id);
      loadTemplates({ search: search || undefined, tag: selectedTag || undefined });
      if (selectedTemplate?._id === template._id) {
        setSelectedTemplate(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Kunde inte ta bort prompt');
    }
  };

  // Filter templates for display (admin sees all, others see only active)
  const normalizedSearch = search.trim().toLowerCase();
  const displayTemplates = templates.filter((t) => {
    if (!isAdmin && !t.isActive) return false;
    if (selectedTag && !(t.tags || []).includes(selectedTag)) return false;
    if (!normalizedSearch) return true;
    const haystack = `${t.name || ''} ${t.description || ''} ${(t.tags || []).join(' ')}`.toLowerCase();
    return haystack.includes(normalizedSearch);
  });

  if (!canUsePrompts) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', p: 3 }}>
        <Paper sx={{ maxWidth: 560, width: '100%', p: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Du har inte behörighet att använda Prompt. Rollen behöver ha <strong>`prompts:use`</strong> eller <strong>`prompts:admin`</strong>.
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={onClose} sx={{ bgcolor: '#1B3967', '&:hover': { bgcolor: '#0f2341' } }}>
              Stäng
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AutoAwesomeIcon sx={{ fontSize: 32, color: '#1B3967' }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1B3967' }}>
              PROMPT
            </Typography>
            {!openAiAvailable && (
              <Chip label="OpenAI ej tillgänglig" color="error" size="small" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Historik">
              <IconButton
                onClick={() => {
                  setShowHistory(true);
                  loadHistory();
                }}
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateDialog(true)}
                sx={{ bgcolor: '#1B3967', '&:hover': { bgcolor: '#0f2341' } }}
              >
                Ny prompt
              </Button>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', p: 2, gap: 2 }}>
        {/* Left: Template buttons */}
        <Paper sx={{ width: 280, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Search */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Sök promptar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {allTags.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {allTags.slice(0, 10).map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant={selectedTag === tag ? 'filled' : 'outlined'}
                    onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                    sx={{ fontSize: '0.7rem' }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Template list */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : displayTemplates.length === 0 ? (
              <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                Inga promptar hittades
              </Typography>
            ) : (
              displayTemplates.map((template) => (
                <Box key={template._id} sx={{ mb: 1 }}>
                  <Button
                    fullWidth
                    variant={selectedTemplate?._id === template._id ? 'contained' : 'outlined'}
                    onClick={() => setSelectedTemplate(template)}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      textTransform: 'none',
                      py: 1.5,
                      bgcolor: selectedTemplate?._id === template._id ? '#1B3967' : undefined,
                      opacity: template.isActive ? 1 : 0.5,
                      '&:hover': {
                        bgcolor: selectedTemplate?._id === template._id ? '#0f2341' : undefined,
                      },
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: selectedTemplate?._id === template._id ? 'white' : 'inherit',
                        }}
                      >
                        {toSentenceCase(template.name)}
                        {!template.isActive && ' (inaktiv)'}
                      </Typography>
                    </Box>
                  </Button>
                  {isAdmin && selectedTemplate?._id === template._id && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, gap: 0.5 }}>
                      <Tooltip title="Redigera">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingTemplate(template);
                            setShowEditDialog(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Box>
        </Paper>

        {/* Right: Input/Output (scrollable for zoomed screens) */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
          {/* Selected template info */}
          {selectedTemplate && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1B3967' }}>
                  {selectedTemplate.name}
                </Typography>
                {runVariants > 1 && (
                  <Chip label={`${runVariants} varianter`} size="small" />
                )}
                {selectedTemplate.outputFormat !== 'text' && (
                  <Chip label={getOutputFormatLabel(selectedTemplate.outputFormat)} size="small" variant="outlined" />
                )}
              </Box>
              {selectedTemplate.description && (
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, color: '#666', fontWeight: 400, fontSize: '0.85rem' }}
                >
                  {selectedTemplate.description}
                </Typography>
              )}
            </Paper>
          )}

          {/* UI notice banner (per template) */}
          {selectedTemplate?.uiNoticeText && (
            <Alert
              severity={selectedTemplate.uiNoticeSeverity || 'warning'}
              sx={{ fontWeight: 500 }}
            >
              {selectedTemplate.uiNoticeText}
            </Alert>
          )}

          {/* Input */}
          <Paper sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Originaltext
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              <PromptFilePicker
                attached={attachedFile}
                onChange={setAttachedFile}
                disabled={generatingGpt}
              />
            </Box>
            <TextField
              fullWidth
              multiline
              placeholder="Klistra in eller skriv texten som AI ska bearbeta..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              sx={{ flex: 1 }}
              slotProps={{
                input: {
                  sx: { height: '100%', alignItems: 'flex-start' },
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Varianter</InputLabel>
                <Select
                  value={runVariants}
                  label="Varianter"
                  onChange={(e) => setRunVariants(Number(e.target.value))}
                >
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="large"
                onClick={handleGenerate}
                disabled={!selectedTemplate || (!inputText.trim() && !attachedFile) || generatingGpt || !openAiAvailable}
                startIcon={generatingGpt ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                sx={{ bgcolor: '#1B3967', '&:hover': { bgcolor: '#0f2341' } }}
              >
                {generatingGpt ? 'Genererar...' : 'Generera AI-svar'}
              </Button>
            </Box>
          </Paper>

          {/* Output */}
          <Paper sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                AI-svar
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
                {outputTexts.length > 1 && (
                  <ToggleButtonGroup
                    value={outputMode}
                    exclusive
                    onChange={(_, val) => val && setOutputMode(val)}
                    size="small"
                  >
                    <ToggleButton value="formatted" sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem' }}>
                      Förhandsvisning
                    </ToggleButton>
                    <ToggleButton value="raw" sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem' }}>
                      Råtext
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
                {outputTexts.length > 0 && (
                  <>
                    <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopyAllForWord}>
                      Kopiera allt till Word
                    </Button>
                    <Button size="small" variant="outlined" onClick={handleCopyAllHtml}>
                      HTML
                    </Button>
                    <Button size="small" variant="outlined" onClick={handleCopyAllRaw}>
                      Råtext
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            {generationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {generationError}
              </Alert>
            )}
            {generationWarning && (
              <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setGenerationWarning(null)}>
                {generationWarning}
              </Alert>
            )}

            {outputTexts.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'textSecondary',
                }}
              >
                <Typography sx={{ textAlign: 'center' }}>
                  Välj en prompt, klistra in originaltext eller ladda upp en PDF/bild och generera ett AI-svar.
                  <br />
                  Här visas en förhandsvisning som kan kopieras till Word eller webb.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {outputTexts.map((text, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      {outputTexts.length > 1 && (
                        <Chip label={`Variant ${index + 1}`} size="small" color="primary" />
                      )}
                      {index === 0 && usedModel && (
                        <Chip label={usedModel} size="small" variant="outlined" />
                      )}
                      <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => handleCopyForWord(text)}>
                        Kopiera till Word
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => handleCopyHtml(text)}>
                        HTML
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => handleCopyRaw(text)}>
                        Råtext
                      </Button>
                    </Box>
                    {outputMode === 'formatted' ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 3,
                          bgcolor: '#fff',
                          maxWidth: 900,
                          mx: 'auto',
                          width: '100%',
                          color: '#1f2a44',
                          '& h1': { fontSize: '1.7rem', fontWeight: 700, mt: 0, mb: 1.5, color: '#1B3967' },
                          '& h2': { fontSize: '1.35rem', fontWeight: 700, mt: 3, mb: 1, color: '#1B3967' },
                          '& h3': { fontSize: '1.15rem', fontWeight: 700, mt: 2.5, mb: 1 },
                          '& h4': { fontSize: '1rem', fontWeight: 700, mt: 2, mb: 0.75 },
                          '& p': { my: 1, lineHeight: 1.6 },
                          '& ul, & ol': { pl: 3, my: 1.25 },
                          '& li': { mb: 0.5 },
                          '& strong': { fontWeight: 600 },
                          '& u': { textUnderlineOffset: '0.16em' },
                          '& del': { fontWeight: 600, color: 'text.secondary' },
                          '& hr': { border: 0, borderTop: '1px solid #d7dce5', my: 2.5 },
                        }}
                        dangerouslySetInnerHTML={{ __html: promptTextToHtml(text) }}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        multiline
                        value={text}
                        InputProps={{ readOnly: true }}
                        minRows={3}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* History dialog */}
      <Dialog open={showHistory} onClose={() => setShowHistory(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Körningshistorik</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : history.length === 0 ? (
            <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
              Ingen historik ännu
            </Typography>
          ) : (
            history.map((run) => (
              <Box
                key={run._id}
                sx={{
                  py: 1,
                  px: 1.5,
                  mb: 0.5,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
                onClick={() => handleLoadHistoryItem(run)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                    {run.templateName}
                  </Typography>
                  {run.error && <Chip label="Fel" color="error" size="small" />}
                </Box>
                {run.summary && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {run.summary}
                  </Typography>
                )}
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                  {new Date(run.createdAt).toLocaleString('sv-SE')}
                  {run.durationMs && ` • ${(run.durationMs / 1000).toFixed(1)}s`}
                  {run.aiModel && ` • ${run.aiModel}`}
                </Typography>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistory(false)}>Stäng</Button>
        </DialogActions>
      </Dialog>

      {/* Create template dialog */}
      <TemplateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSave={handleCreateTemplate}
        saving={saving}
        title="Skapa ny prompt"
      />

      {/* Edit template dialog */}
      {editingTemplate && (
        <TemplateDialog
          open={showEditDialog}
          onClose={() => {
            setShowEditDialog(false);
            setEditingTemplate(null);
          }}
          onSave={handleUpdateTemplate}
          onDelete={() => handleDeleteTemplate(editingTemplate)}
          saving={saving}
          title="Redigera prompt"
          initialValues={editingTemplate}
        />
      )}
      <Snackbar
        open={Boolean(copyMessage)}
        autoHideDuration={2500}
        onClose={() => setCopyMessage(null)}
        message={copyMessage}
      />
    </Box>
  );
};

// =============================================
// Template Dialog Component
// =============================================

interface TemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (params: CreateTemplateParams) => void;
  onDelete?: () => void;
  saving: boolean;
  title: string;
  initialValues?: PromptTemplate;
}

const TemplateDialog: React.FC<TemplateDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  saving,
  title,
  initialValues,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [aiModel, setAiModel] = useState(DEFAULT_PROMPT_MODEL);
  const [temperature, setTemperature] = useState(0.7);
  const [maxOutputTokens, setMaxOutputTokens] = useState(DEFAULT_MAX_OUTPUT_TOKENS);
  const [outputFormat, setOutputFormat] = useState<'text' | 'markdown' | 'json'>('text');
  const [variants, setVariants] = useState(1);
  const [tags, setTags] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [uiNoticeText, setUiNoticeText] = useState('');
  const [uiNoticeSeverity, setUiNoticeSeverity] = useState<'info' | 'warning' | 'error' | 'success'>('warning');

  // TipTap editor for rich text system prompt
  const promptEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Placeholder.configure({
        placeholder: 'Beskriv hur AI ska bearbeta texten. Ex: "Du är en copywriter för sociala medier..."',
      }),
    ],
    content: '',
    onUpdate: () => {
      // Content is read on submit via getHTML()
    },
  }, [open]);

  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name);
      setDescription(initialValues.description || '');
      setAiModel(initialValues.aiModel || DEFAULT_PROMPT_MODEL);
      setTemperature(initialValues.temperature);
      setMaxOutputTokens(initialValues.maxOutputTokens);
      setOutputFormat(initialValues.outputFormat);
      setVariants(initialValues.variants);
      setTags(initialValues.tags.join(', '));
      setSortOrder(initialValues.sortOrder);
      setIsActive(initialValues.isActive);
      setUiNoticeText(initialValues.uiNoticeText || '');
      setUiNoticeSeverity(initialValues.uiNoticeSeverity || 'warning');
      // Set editor content (could be HTML or Markdown)
      if (promptEditor) {
        const content = initialValues.systemPrompt || '';
        if (content.startsWith('<')) {
          promptEditor.commands.setContent(DOMPurify.sanitize(content));
        } else {
          // Convert stored Markdown to rich-text HTML for the editor
          promptEditor.commands.setContent(markdownToEditorHtml(content));
        }
      }
    } else {
      setName('');
      setDescription('');
      setAiModel(DEFAULT_PROMPT_MODEL);
      setTemperature(0.7);
      setMaxOutputTokens(DEFAULT_MAX_OUTPUT_TOKENS);
      setOutputFormat('text');
      setVariants(1);
      setTags('');
      setSortOrder(0);
      setIsActive(true);
      setUiNoticeText('');
      setUiNoticeSeverity('warning');
      if (promptEditor) {
        promptEditor.commands.setContent('');
      }
    }
    setShowAdvanced(false);
  }, [initialValues, open, promptEditor]);

  // Convert HTML to Markdown for better OpenAI understanding
  const getSystemPromptText = (): string => {
    if (!promptEditor) return '';
    const html = promptEditor.getHTML();
    
    // HTML to Markdown conversion
    let md = html
      // Headings
      .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n')
      .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n')
      .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n')
      // Bold and italic
      .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
      .replace(/<b>(.*?)<\/b>/gi, '**$1**')
      .replace(/<em>(.*?)<\/em>/gi, '*$1*')
      .replace(/<i>(.*?)<\/i>/gi, '*$1*')
      .replace(/<u>(.*?)<\/u>/gi, '++$1++')
      .replace(/<del>(.*?)<\/del>/gi, '~~$1~~')
      .replace(/<s>(.*?)<\/s>/gi, '~~$1~~')
      // Lists - process list items first
      .replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n')
      .replace(/<\/ul>/gi, '\n')
      .replace(/<\/ol>/gi, '\n')
      .replace(/<ul[^>]*>/gi, '\n')
      .replace(/<ol[^>]*>/gi, '\n')
      // Blockquote
      .replace(/<blockquote[^>]*>(.*?)<\/blockquote>/gi, '> $1\n')
      // Line breaks and paragraphs
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p>/gi, '\n\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/p>/gi, '\n\n')
      // Remove remaining tags
      .replace(/<[^>]+>/g, '')
      // HTML entities
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      // Clean up whitespace
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]+\n/g, '\n')
      .trim();
    
    return md;
  };

  const handleSubmit = () => {
    const systemPrompt = getSystemPromptText();
    onSave({
      name,
      description: description || undefined,
      systemPrompt,
      model: aiModel,
      temperature,
      maxOutputTokens,
      outputFormat,
      variants,
      tags: tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      sortOrder,
      isActive,
      uiNoticeText: uiNoticeText.trim() || undefined,
      uiNoticeSeverity: uiNoticeText.trim() ? uiNoticeSeverity : undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Namn (visas på knappen)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          sx={{ mt: 1, mb: 2 }}
        />

        <TextField
          fullWidth
          label="Beskrivning (valfritt)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Rich text editor for System Prompt */}
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
          System Prompt (instruktionen till AI) *
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            mb: 2,
            '& .ProseMirror': {
              minHeight: 200,
              maxHeight: 400,
              overflowY: 'auto',
              p: 2,
              outline: 'none',
              '& p': { margin: '0.5em 0' },
              '& ul, & ol': { paddingLeft: '1.5em', margin: '0.5em 0' },
              '& li': { margin: '0.25em 0' },
              '& h1, & h2, & h3': { margin: '0.75em 0 0.5em' },
              '& blockquote': {
                borderLeft: '3px solid #ccc',
                paddingLeft: '1em',
                margin: '0.5em 0',
                color: '#666',
              },
            },
            '& .ProseMirror p.is-editor-empty:first-child::before': {
              color: '#aaa',
              content: 'attr(data-placeholder)',
              float: 'left',
              height: 0,
              pointerEvents: 'none',
            },
          }}
        >
          {/* Toolbar */}
          <Box sx={{ borderBottom: '1px solid #e0e0e0', p: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <ToggleButtonGroup size="small">
              <ToggleButton
                value="bold"
                selected={promptEditor?.isActive('bold') ?? false}
                onClick={() => promptEditor?.chain().focus().toggleBold().run()}
              >
                <FormatBoldIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton
                value="italic"
                selected={promptEditor?.isActive('italic') ?? false}
                onClick={() => promptEditor?.chain().focus().toggleItalic().run()}
              >
                <FormatItalicIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton
                value="underline"
                selected={promptEditor?.isActive('underline') ?? false}
                onClick={() => promptEditor?.chain().focus().toggleUnderline().run()}
              >
                <FormatUnderlinedIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small">
              <ToggleButton
                value="bulletList"
                selected={promptEditor?.isActive('bulletList') ?? false}
                onClick={() => promptEditor?.chain().focus().toggleBulletList().run()}
              >
                <FormatListBulletedIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton
                value="orderedList"
                selected={promptEditor?.isActive('orderedList') ?? false}
                onClick={() => promptEditor?.chain().focus().toggleOrderedList().run()}
              >
                <FormatListNumberedIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton size="small" onClick={() => promptEditor?.chain().focus().undo().run()} disabled={!promptEditor?.can().undo()}>
              <UndoIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => promptEditor?.chain().focus().redo().run()} disabled={!promptEditor?.can().redo()}>
              <RedoIcon fontSize="small" />
            </IconButton>
          </Box>
          <EditorContent editor={promptEditor} />
        </Paper>

        <TextField
          fullWidth
          label="Taggar (kommaseparerade)"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="Ex: sociala medier, vin, instagram"
          sx={{ mb: 2 }}
        />

        {/* Per-template UI notice (shown to users above input) */}
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
          Viktig info (syns för användare ovanför input-fältet)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            value={uiNoticeText}
            onChange={(e) => setUiNoticeText(e.target.value)}
            placeholder="Ex: Glöm inte att inkludera årgång och region i texten!"
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Nivå</InputLabel>
            <Select
              value={uiNoticeSeverity}
              onChange={(e) => setUiNoticeSeverity(e.target.value as any)}
              label="Nivå"
            >
              <MenuItem value="info">Info (blå)</MenuItem>
              <MenuItem value="warning">Varning (gul)</MenuItem>
              <MenuItem value="error">Viktigt (röd)</MenuItem>
              <MenuItem value="success">OK (grön)</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {uiNoticeText.trim() && (
          <Alert severity={uiNoticeSeverity} sx={{ mb: 2 }}>
            {uiNoticeText}
          </Alert>
        )}

        <Button
          onClick={() => setShowAdvanced(!showAdvanced)}
          startIcon={<SettingsIcon />}
          sx={{ mb: 2 }}
        >
          {showAdvanced ? 'Dölj avancerat' : 'Visa avancerat'}
        </Button>

        {showAdvanced && (
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Modell</InputLabel>
                <Select value={aiModel} onChange={(e) => setAiModel(e.target.value)} label="Modell">
                  <MenuItem value="gpt-5.5">GPT 5.5 (standard)</MenuItem>
                  <MenuItem value="gpt-5.4">GPT 5.4 (äldre)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                type="number"
                label="Svarslängd (tokens)"
                value={maxOutputTokens}
                onChange={(e) => setMaxOutputTokens(Number(e.target.value))}
                helperText="Höj för längre recept"
                inputProps={{ min: 100, max: 16000, step: 100 }}
                sx={{ width: 190 }}
              />

              <TextField
                type="number"
                label="Kreativitet"
                value={temperature}
                onChange={(e) => setTemperature(Number(e.target.value))}
                helperText="0 = strikt, 2 = fritt"
                inputProps={{ min: 0, max: 2, step: 0.1 }}
                sx={{ width: 160 }}
              />

              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Svarstyp</InputLabel>
                <Select
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value as any)}
                  label="Svarstyp"
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="markdown">Formaterad text</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Varianter</InputLabel>
                <Select
                  value={variants}
                  onChange={(e) => setVariants(Number(e.target.value))}
                  label="Varianter"
                >
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={4}>4</MenuItem>
                  <MenuItem value={5}>5</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="number"
                label="Sorteringsordning"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
                size="small"
                sx={{ width: 150 }}
                helperText="Lägre nummer visas först"
              />

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={isActive ? 'active' : 'inactive'}
                  onChange={(e) => setIsActive(e.target.value === 'active')}
                  label="Status"
                  size="small"
                >
                  <MenuItem value="active">Aktiv</MenuItem>
                  <MenuItem value="inactive">Inaktiv</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {onDelete && (
          <Button color="error" onClick={onDelete} sx={{ mr: 'auto' }}>
            Inaktivera
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>
          Avbryt
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name || promptEditor?.isEmpty || saving}
          sx={{ bgcolor: '#1B3967' }}
        >
          {saving ? <CircularProgress size={20} /> : 'Spara'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptsPage;
